/**
 * StaffObwModal — the staff-dashboard MOUNT for the §9-forked OBW engine
 * (ADR-076).
 *
 * This is the context-bearing layer: it owns the launch context and the return
 * target, and the journey carries neither (ADR-076 core principle). The Nexa
 * consultant launches a journey from a dashboard card; it renders IN PLACE as a
 * modal overlay over the staff dashboard, and on completion or exit the modal is
 * dismissed in place with a toast on the dashboard the consultant was already on
 * — never a subscriber-portal tab, never a portal flip (UAT U1/U3/U4).
 *
 * For BOW the mount mints an HMAC launch token (POST /admin/bow/launch) and
 * seeds it so bow-complete provisions via portal-api POST /portal/bow/provision.
 * Identity is the mount's: the consultant prefill comes from the staff session,
 * and all trust is enforced server-side at the provision route by the token.
 */
import { useCallback, useEffect, useState } from 'react';
import { useToast, toastFromError } from '../components/ToastProvider';
import { ENV } from '../amplify-config';
import { launchBow, launchSow, launchB2s } from '../lib/beta-sites-api';
import { HOME_ZONE, ZONES, consoleZoneLabel, type PortalZone } from '../lib/zones';
import { loadZoneMap, readZoneGate } from '../lib/zone-map';
import type { ResidencyConfirm, ResidencyContext, ZoneMap } from './residency';
import { OBWWizard, type ObwCompletionResult } from './OBWWizard';
import type { JourneyId, ObwStepId } from './journeys';
import { assessSowMount } from './fep';
import { useZoneWriteReport } from '../components/ZoneContext';

type PartialStepData = Partial<Record<ObwStepId, Record<string, unknown>>>;
type MintState = 'minting' | 'ready' | 'error';

/** ADR-098 — the result of a successful BOW provision, surfaced to the mount so
 *  a Beta Interest lead can be linked to the account it produced. */
export interface StaffObwProvisionResult {
  accountId?: string;
  customerNumber?: string;
}

interface StaffObwModalProps {
  journeyId: JourneyId;
  /**
   * B2S only — the zone of the EXISTING beta account being upgraded (its own zone, resolved by
   * the caller). Omitted = the home zone.
   *
   * ⭐⭐ BOW AND SOW NO LONGER TAKE A ZONE FROM THEIR CALLER (ADR-124 D5). The zone a new account
   * is created in is decided by the CUSTOMER's country, at the journey's `residency` step — not by
   * which zone the staff member happens to be working in. So those journeys mint nothing when
   * they open: the launch token is minted in the derived zone only once the country is confirmed
   * (see `confirmResidency`), and the provision origin rides the same seed, so the mint and the
   * account can never land in different zones.
   */
  zone?: PortalZone;
  /**
   * BOW / SOW — the console's working zone (ADR-123 D3 precedence), so the residency step can
   * say "This account will be created in {Zone}." when the customer's country derives another.
   */
  workingZone?: PortalZone;
  /** BOW / SOW — a country to PRE-FILL the residency step with (a Beta Interest lead's declared
   *  country). Pre-fill only: the consultant still confirms it. */
  residencyPrefill?: string;
  /** Consultant prefill — the launching staff member's display name. */
  consultantName?: string;
  /** B2S (Upgrade) only — the beta account being upgraded. The mount mints the
   *  HMAC B2S launch token for this account (POST /admin/b2s/launch). Required
   *  when journeyId === 'b2s'; ignored otherwise. */
  b2sAccountId?: string;
  /** ADR-098 — BOW prefill from a Beta Interest lead. The consultant can still
   *  edit every field; this only saves re-typing what the lead already told us. */
  leadPrefill?: {
    businessName?: string;
    domainUrl?: string;
    ownerName?: string;
    email?: string;
  };
  /** Dismiss the modal (return to the staff dashboard, in place). */
  onClose: () => void;
  /** Fired after a participant is successfully provisioned, so the dashboard
   *  can refresh the beta-participants panel. For BOW it carries the created
   *  account id/customer number (ADR-098 — link the lead to the account). */
  onProvisioned?: (result?: StaffObwProvisionResult) => void;
}

export function StaffObwModal({
  journeyId, zone, workingZone, residencyPrefill, consultantName, b2sAccountId, leadPrefill, onClose, onProvisioned,
}: StaffObwModalProps) {
  const toast = useToast();
  const [mintState, setMintState] = useState<MintState>('minting');
  const [seed, setSeed] = useState<PartialStepData>({});
  // ADR-124 — the served map, loaded by the MOUNT (the journey holds no transport).
  const [zoneMap, setZoneMap] = useState<ZoneMap | null>(null);
  const createsHome = journeyId === 'bow' || journeyId === 'sow';
  // B2S acts on an EXISTING account in exactly one zone — the participant's own — so while the
  // upgrade is open the navigation bar names it ("Editing us-east-1 · United States"), the same
  // statement every other zone-bound write makes (ADR-123). BOW/SOW report nothing here: their
  // zone is not known until the residency step, which states it itself.
  useZoneWriteReport(journeyId === 'b2s' ? (zone ?? HOME_ZONE).code : null);

  /**
   * ⭐⭐ ADR-124 D5/D6 — THE MINT HAPPENS HERE, IN THE ZONE THE CUSTOMER'S COUNTRY DERIVES.
   *
   * The residency step calls this port with the confirmed country and the zone it maps to. It
   * refuses a zone this console cannot create in (no portal origin — the same capability check
   * `OBW_CREATE_ZONES` always made), stops a BOW whose zone has its beta-invites gate shut (the
   * consultant is stopped on the form, never after creation — D6), and otherwise mints the
   * launch token on THAT zone's admin API and returns it with THAT zone's portal origin, which
   * flow through to the completion step together. Nothing about the working zone is used.
   */
  const confirmResidency = useCallback(
    async (_jurisdiction: string, zoneCode: string): Promise<ResidencyConfirm> => {
      // The same capability rule the mount always applied: a zone needs its own portal origin to
      // host a journey; only the HOME zone may fall back to the bundle constant (never widened —
      // see the ⛔ note in `mint` below).
      const target = ZONES.find((z) => z.code === zoneCode);
      const portalApiBase = target
        ? target.portalApiBaseUrl ?? (target.home ? ENV.PORTAL_API_BASE_URL : undefined)
        : undefined;
      if (!target || !portalApiBase) {
        console.error('StaffObwModal: the derived zone cannot host this journey', { zone: zoneCode });
        toast.push({
          variant: 'error',
          title: 'Onboarding unavailable',
          detail: 'This environment isn’t configured for onboarding in that zone yet. Please contact platform support.',
        });
        return { ok: false, reason: 'error' };
      }
      try {
        if (journeyId === 'bow') {
          if ((await readZoneGate('beta', zoneCode)) !== 'open') {
            return { ok: false, reason: 'gate_closed', zone: zoneCode };
          }
          const { token } = await launchBow(target);
          return { ok: true, seed: { bowToken: token, portalApiBase } };
        }
        const { token } = await launchSow(target);
        // FEP (ADR-080) — the auth-path SOW context, as before; trust IS the launch token.
        const fep = assessSowMount({ launchToken: token, nexaConsultant: consultantName });
        const sowToken = fep.provisioning.trigger === 'launch-token' ? fep.provisioning.token : '';
        // The consultant comes from the FEP-resolved context too (it is the source of truth for
        // the auth path); it flattens to the `profile` step exactly as the old mount seed did.
        return { ok: true, seed: { sowToken, portalApiBase, nexaConsultant: fep.nexaConsultant ?? '' } };
      } catch (err) {
        console.error('StaffObwModal: launch failed', err);
        const { title, detail } = toastFromError(err, 'Could not start onboarding');
        toast.push({ variant: 'error', title, detail });
        return { ok: false, reason: 'error' };
      }
    },
    [journeyId, consultantName, toast],
  );

  const mint = useCallback(async () => {
    setMintState('minting');
    // Resolved once, and used for BOTH origins below — so the mint and the provision can never
    // disagree about which jurisdiction this journey belongs to.
    // ⚠ HOME_ZONE here is justified, not a default to convert (ADR-123 sweep): every account-
    // creating or account-acting caller passes the zone (Home's create target, the lead's zone,
    // the upgrading participant's zone). The only caller that omits it is `partner-apply`, which
    // creates no account in any zone at mount time — see Home.tsx and ADR-122 finding #2.
    const target = zone ?? HOME_ZONE;
    // The fork provisions through portal-api; if this environment's staff build
    // wasn't given VITE_PORTAL_API_BASE_URL (deploy.yml export rides with the
    // provisioning PR), fail fast with a clear message rather than dead-ending
    // at bow-complete.
    // ⚠ THE GUARD IS ON THE TARGET ZONE'S ORIGIN, NOT THE BUILD'S. A zone with no portal-api
    // origin cannot complete a journey, and finding that out at the completion step — after the
    // consultant has entered the subscriber's details — is exactly the dead end
    // `OBW_CREATE_ZONES` filters the chooser to avoid. This is the second half of that guard,
    // for the paths that reach here without going through the chooser.
    //
    // ⛔⛔ THE FALL-BACK TO THE BUNDLE CONSTANT IS FOR THE HOME ZONE **ONLY**, AND WIDENING IT
    // WOULD REBUILD THE EXACT DEFECT THIS PROP EXISTS TO REMOVE. A plain
    // `target.portalApiBaseUrl ?? ENV.PORTAL_API_BASE_URL` reads as harmless belt-and-braces and
    // is not: for a NAMED zone that has no portal origin it silently substitutes the HOME zone's,
    // so the mint goes to that zone and the account is created in this one — a half-routed
    // journey arriving through a different door, with the guard below never firing because a
    // value was found. A named zone must carry its own origin or be refused.
    //
    // ⭐ FOUND BY MUTATION, NOT BY REVIEW. The first draft had the plain `??`, and the test
    // suite was green over it — because `ENV.PORTAL_API_BASE_URL` is undefined under vitest, so
    // the wrong expression and the right one agreed. `zone-create-target.test.tsx` now pins the
    // constant to a distinct value precisely so this line can be wrong out loud.
    const portalApiBase = target.portalApiBaseUrl ?? (target.home ? ENV.PORTAL_API_BASE_URL : undefined);
    // BOW/SOW resolve their origin per derived zone in `confirmResidency`; this guard is B2S's.
    if (!portalApiBase && journeyId === 'b2s') {
      console.error('StaffObwModal: no portal-api origin for the target zone', { zone: target.code });
      toast.push({
        variant: 'error',
        title: 'Beta onboarding unavailable',
        detail: 'This environment isn’t configured for beta onboarding yet. Please contact platform support.',
      });
      setMintState('error');
      return;
    }
    try {
      // POW (partner-apply) is subscriber-keyed, NOT account-less: the token is
      // minted PER SUBSCRIBER inside pa-pick-subscriber once the consultant picks
      // one — so there is nothing to mint at mount. Render the wizard directly;
      // the picker is the first step.
      if (journeyId === 'partner-apply') {
        setSeed({});
        setMintState('ready');
        return;
      }
      // B2S (Upgrade) — mint the HMAC B2S launch token for the target beta
      // account (POST /admin/b2s/launch carries the accountId). Seed the token +
      // businessName into `subscribe`; they flatten through to b2s-complete, which
      // converts via /portal/b2s/convert. The engine stays account-less (accountId
      // '') — trust is the token, validated server-side (ADR-078 §4 / C3).
      if (journeyId === 'b2s') {
        if (!b2sAccountId) {
          throw new Error('No account selected for upgrade.');
        }
        // ⛔ BOTH HALVES IN THE PARTICIPANT'S OWN ZONE (ADR-124 follow-up, 30 September 2026).
        // The mint used to omit `target`, so it went to the home zone — which holds no such
        // account for a US participant — and the convert step posted to the bundle's build-time
        // origin (always AU). A US participant's upgrade would have been attempted, and billed,
        // in Australia. The mint now names the zone, and the convert origin is seeded with it,
        // exactly as BOW/SOW seed theirs.
        const { token, businessName } = await launchB2s(b2sAccountId, target);
        setSeed({ subscribe: { b2sToken: token, businessName, portalApiBase } });
        setMintState('ready');
        return;
      }
      // BOW/SOW are account-less: mint the journey's launch token here so its TTL
      // starts when the consultant opens the journey. The token is seeded into the
      // journey's first step + flows (flattened) to the completion step's
      // provision call; the consultant prefill comes from the staff session.
      // ADR-124 — BOW/SOW mint NOTHING on open: the zone is not known until the residency step.
      // Load the served map (for the step and its validator) and seed only what the consultant
      // would otherwise re-type; `confirmResidency` mints once the country is confirmed.
      const map = await loadZoneMap();
      setZoneMap(map);
      if (journeyId === 'sow') {
        // The consultant prefill arrives with the FEP-resolved launch, at confirm (see above).
        setSeed({});
      } else {
        // ADR-098 — seed the lead's details so "Provision beta" launches BOW pre-filled.
        setSeed({
          ...(leadPrefill?.domainUrl ? { 'domain-verify': { domainUrl: leadPrefill.domainUrl } } : {}),
          profile: {
            nexaConsultant: consultantName ?? '',
            ...(leadPrefill?.businessName ? { businessName: leadPrefill.businessName } : {}),
            ...(leadPrefill?.ownerName ? { ownerName: leadPrefill.ownerName } : {}),
            ...(leadPrefill?.email ? { businessEmail: leadPrefill.email } : {}),
          },
        });
      }
      setMintState('ready');
    } catch (err) {
      // Error-surfacing standard — never the raw error; log + friendly toast.
      console.error('StaffObwModal: launch failed', err);
      const { title, detail } = toastFromError(err, 'Could not start onboarding');
      toast.push({ variant: 'error', title, detail });
      setMintState('error');
    }
  }, [journeyId, zone, consultantName, b2sAccountId, leadPrefill, toast]);

  const residency: ResidencyContext | undefined = createsHome
    ? {
        map: zoneMap,
        prefill: residencyPrefill,
        workingZone: workingZone ? { code: workingZone.code, label: consoleZoneLabel(workingZone.code) } : undefined,
        confirm: confirmResidency,
      }
    : undefined;

  useEffect(() => { void mint(); }, [mint]);

  const handleComplete = useCallback(
    (result: ObwCompletionResult) => {
      const d = result.stepData;
      // B2S (Upgrade) — the beta account was converted to a paying subscriber in
      // place. Dismiss the modal and return to the nexa-admin dashboard with a
      // success toast (C3 / ADR-077 mount-owns-exit); the panel refresh drops the
      // now-non-beta row. No subscriber-portal navigation.
      if (result.journeyId === 'b2s') {
        const org = (d.businessName as string) || 'The beta account';
        toast.push({
          variant: 'success',
          title: 'Upgraded to subscriber',
          detail: `${org} is now a paying subscriber — it no longer appears on the Beta participants table.`,
        });
        onProvisioned?.();
        onClose();
        return;
      }
      // POW (partner-apply) — a partner application was submitted for the picked
      // subscriber; no account creation / activation email.
      if (result.journeyId === 'partner-apply') {
        const org = (d.pickedOrg as string) || (d.businessName as string) || 'the subscriber';
        toast.push({
          variant: 'success',
          title: 'Partner application submitted',
          detail: `${org} — suitability assessment running. Track it in the Partners queue.`,
        });
        onProvisioned?.();
        onClose();
        return;
      }
      const org = (d.businessName as string) || 'The participant';
      const cust = d.provisionedCustomerNumber as string | undefined;
      const acct = d.provisionedAccountId as string | undefined;
      const emailOk = d.activationEmailSent !== false;
      const noun = result.journeyId === 'sow' ? 'Subscriber' : 'Beta participant';
      toast.push({
        variant: emailOk ? 'success' : 'info',
        title: `${noun} onboarded`,
        detail: emailOk
          ? `${org}${cust ? ` (Customer № ${cust})` : ''} — activation email sent.`
          : `${org} created, but the activation email did not send — resend from the dashboard.`,
      });
      // ADR-098 — hand the created account back so a Beta Interest lead can be
      // flipped to `provisioned` and linked. SOW carries no account link here.
      onProvisioned?.(result.journeyId === 'sow' ? undefined : { accountId: acct, customerNumber: cust });
      onClose();
    },
    [toast, onProvisioned, onClose],
  );

  const handleExit = useCallback(() => {
    toast.push({ variant: 'info', title: 'Onboarding closed', detail: 'No changes were made.' });
    onClose();
  }, [toast, onClose]);

  if (mintState === 'minting') {
    return (
      <div className="obw-overlay" role="dialog" aria-modal="true" aria-label="Starting onboarding">
        <div className="obw-modal">
          <div className="obw-body">
            <div className="obw-analysis-loading" aria-live="polite">Starting beta onboarding…</div>
          </div>
        </div>
      </div>
    );
  }

  if (mintState === 'error') {
    return (
      <div className="obw-overlay" role="dialog" aria-modal="true" aria-label="Onboarding error">
        <div className="obw-modal">
          <button type="button" className="obw-close" onClick={onClose} aria-label="Close">×</button>
          <div className="obw-body">
            <div className="portal-wizard-error" role="alert">
              Could not start beta onboarding. Please try again.
            </div>
            <button type="button" className="obw-btn-primary" onClick={() => void mint()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <OBWWizard
      journeyId={journeyId}
      accountId=""
      actor="nexa-admin"
      initialStepData={seed}
      onComplete={handleComplete}
      onExit={handleExit}
      residency={residency}
    />
  );
}
